import axios from 'axios';

const LOCAL_BACKEND_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const resolveApiBaseUrl = () => {
  const configuredUrl = process.env.REACT_APP_BACKEND_URL?.trim();

  if (typeof window === 'undefined') {
    return configuredUrl || 'http://localhost:8001';
  }

  const browserProtocol = window.location.protocol;
  const browserHostname = window.location.hostname;
  const defaultBackendUrl = `${browserProtocol}//${browserHostname}:8001`;

  if (!configuredUrl) {
    return defaultBackendUrl;
  }

  try {
    const url = new URL(configuredUrl);
    if (!LOCAL_BACKEND_HOSTS.has(browserHostname) && LOCAL_BACKEND_HOSTS.has(url.hostname)) {
      url.hostname = browserHostname;
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    return configuredUrl.replace(/\/$/, '');
  }
};

const API_URL = resolveApiBaseUrl();
const API = `${API_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('hrms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('hrms_token');
      localStorage.removeItem('hrms_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Services
export const authService = {
  login: async (username, password) => {
    const response = await apiClient.post('/auth/login', { username, password });
    return response.data;
  },
  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  changePassword: async (data) => {
    const response = await apiClient.post('/users/change-password', data);
    return response.data;
  },
  requestOtp: async (identifier) => {
    const response = await apiClient.post('/auth/request-otp', { identifier });
    return response.data;
  },
  verifyOtp: async (identifier, otp) => {
    const response = await apiClient.post('/auth/verify-otp', { identifier, otp });
    return response.data;
  },
};

// Employee Services
export const employeeService = {
  list: async (params) => {
    const response = await apiClient.get('/employees', { params });
    return response.data;
  },
  get: async (id) => {
    const response = await apiClient.get(`/employees/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/employees', data);
    return response.data;
  },
  import: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/employees/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/employees/${id}`, data);
    return response.data;
  },
  offboard: async (id) => {
    const response = await apiClient.post(`/employees/${id}/offboard`);
    return response.data;
  },
};

// Location Services
export const locationService = {
  list: async () => {
    const response = await apiClient.get('/locations');
    return response.data;
  },
  get: async (id) => {
    const response = await apiClient.get(`/locations/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/locations', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/locations/${id}`, data);
    return response.data;
  },
  assignEmployees: async (locationId, employeeIds) => {
    const response = await apiClient.post(`/locations/${locationId}/assign-employees`, employeeIds);
    return response.data;
  },
};

// Attendance Services
export const attendanceService = {
  checkIn: async (data) => {
    const response = await apiClient.post('/attendance/check-in', data);
    return response.data;
  },
  livenessCheck: async (videoBlob) => {
    const formData = new FormData();
    formData.append('video', videoBlob, 'liveness.webm');
    const response = await apiClient.post('/attendance/liveness-check', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  checkOut: async (data) => {
    const response = await apiClient.post('/attendance/check-out', data);
    return response.data;
  },
  getMyAttendance: async (month, year) => {
    const response = await apiClient.get('/attendance/my-attendance', { params: { month, year } });
    return response.data;
  },
  getTodayAttendance: async () => {
    const response = await apiClient.get('/attendance/today');
    return response.data;
  },
  validateGeofence: async (employeeId, latitude, longitude) => {
    const response = await apiClient.post('/attendance/validate-geofence', {
      employee_id: employeeId,
      latitude,
      longitude,
    });
    return response.data;
  },
  createCorrectionRequest: async (data) => {
    const response = await apiClient.post('/attendance/correction-request', data);
    return response.data;
  },
  getCorrectionRequests: async (status) => {
    const response = await apiClient.get('/attendance/correction-requests', { params: { status } });
    return response.data;
  },
  approveCorrectionRequest: async (id) => {
    const response = await apiClient.post(`/attendance/correction-requests/${id}/approve`);
    return response.data;
  },
  rejectCorrectionRequest: async (id, reason) => {
    const response = await apiClient.post(`/attendance/correction-requests/${id}/reject`, null, {
      params: { rejection_reason: reason },
    });
    return response.data;
  },
  edit: async (id, data) => {
    const response = await apiClient.patch(`/attendance/${id}/edit`, data);
    return response.data;
  },
  getLogs: async (params) => {
    const response = await apiClient.get('/attendance/logs', { params });
    return response.data;
  },
};

// Leave Services
export const leaveService = {
  apply: async (data) => {
    const response = await apiClient.post('/leaves/apply', data);
    return response.data;
  },
  getMyLeaves: async (status) => {
    const response = await apiClient.get('/leaves/my-leaves', { params: { status } });
    return response.data;
  },
  getMyBalance: async () => {
    const response = await apiClient.get('/leaves/my-balance');
    return response.data;
  },
  getPendingApprovals: async () => {
    const response = await apiClient.get('/leaves/pending-approvals');
    return response.data;
  },
  approve: async (id) => {
    const response = await apiClient.post(`/leaves/${id}/approve`);
    return response.data;
  },
  reject: async (id, reason) => {
    const response = await apiClient.post(`/leaves/${id}/reject`, null, {
      params: { rejection_reason: reason },
    });
    return response.data;
  },
  updateBalance: async (employeeId, data) => {
    const response = await apiClient.patch(`/leave-balance/${employeeId}`, data);
    return response.data;
  },
  importBalances: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/leave-balance/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getAllBalances: async () => {
    const response = await apiClient.get('/leaves/all-balances');
    return response.data;
  },
};

// Holiday Services
export const holidayService = {
  list: async () => {
    const response = await apiClient.get('/holidays');
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/holidays', data);
    return response.data;
  },
};

// Dashboard Services
export const dashboardService = {
  getStats: async () => {
    const response = await apiClient.get('/dashboard/stats');
    return response.data;
  },
  getEmployeeStats: async () => {
    const response = await apiClient.get('/dashboard/employee-stats');
    return response.data;
  },
  getAttendanceTrends: async (days = 30) => {
    const response = await apiClient.get('/dashboard/attendance-trends', { params: { days } });
    return response.data;
  },
  getDepartmentStats: async () => {
    const response = await apiClient.get('/dashboard/department-stats');
    return response.data;
  },
};

// Payroll Services
export const payrollService = {
  getSalaryStructure: async (employeeId) => {
    const response = await apiClient.get(`/payroll/salary-structure/${employeeId}`);
    return response.data;
  },
  createSalaryStructure: async (data) => {
    const response = await apiClient.post('/payroll/salary-structure', data);
    return response.data;
  },
  getRuns: async () => {
    const response = await apiClient.get('/payroll/runs');
    return response.data;
  },
  runPayroll: async (data) => {
    const response = await apiClient.post('/payroll/run', data);
    return response.data;
  },
  finalizePayroll: async (runId) => {
    const response = await apiClient.post(`/payroll/finalize/${runId}`);
    return response.data;
  },
  getMyPayslips: async () => {
    const response = await apiClient.get('/payroll/payslips/my');
    return response.data;
  },
  downloadPayslip: async (payslipId) => {
    const response = await apiClient.get(`/payroll/payslips/${payslipId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
  getEpfReport: async (month, year) => {
    const response = await apiClient.get('/payroll/epf-report', {
      params: { month, year },
    });
    return response.data;
  },
  downloadEpfReport: async (month, year) => {
    const response = await apiClient.get('/payroll/epf-report', {
      params: { month, year },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Analytics Services
export const analyticsService = {
  getPunctualityScore: async (employeeId, month, year) => {
    const response = await apiClient.get(`/analytics/punctuality/${employeeId}`, {
      params: { month, year },
    });
    return response.data;
  },
  getAnomalies: async () => {
    const response = await apiClient.get('/analytics/anomalies');
    return response.data;
  },
  detectAnomalies: async (employeeId, days = 30) => {
    const response = await apiClient.post(`/analytics/detect-anomalies/${employeeId}`, null, {
      params: { days },
    });
    return response.data;
  },
};

// Reports Services
export const reportsService = {
  getAttendanceReport: async (startDate, endDate, department) => {
    const response = await apiClient.get('/reports/attendance', {
      params: { start_date: startDate, end_date: endDate, department },
    });
    return response.data;
  },
  getLateArrivalsReport: async (startDate, endDate) => {
    const response = await apiClient.get('/reports/late-arrivals', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
  getLeaveSummaryReport: async (year) => {
    const response = await apiClient.get('/reports/leave-summary', { params: { year } });
    return response.data;
  },
};

// Users Services
export const usersService = {
  list: async () => {
    const response = await apiClient.get('/users');
    return response.data;
  },
  updateRole: async (userId, role) => {
    const response = await apiClient.put(`/users/${userId}/role`, null, {
      params: { new_role: role },
    });
    return response.data;
  },
  toggleStatus: async (userId) => {
    const response = await apiClient.put(`/users/${userId}/toggle-status`);
    return response.data;
  },
  update: async (userId, data) => {
    const response = await apiClient.patch(`/users/${userId}`, data);
    return response.data;
  },
  resetPassword: async (userId) => {
    const response = await apiClient.post(`/users/${userId}/reset-password`);
    return response.data;
  },
  unlockUser: async (userId) => {
    const response = await apiClient.post(`/users/${userId}/unlock`);
    return response.data;
  },
};

// Notification Services
export const notificationService = {
  getCount: async () => {
    const response = await apiClient.get('/notifications/count');
    return response.data;
  },
};

// Audit Services
export const auditService = {
  getLogs: async (resourceType, userId, limit = 100) => {
    const response = await apiClient.get('/audit-logs', {
      params: { resource_type: resourceType, user_id: userId, limit },
    });
    return response.data;
  },
};

export default apiClient;
